import React, { useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  ChevronDown as ChevronDownIcon,
  Folder as FolderIcon,
  User as UserIcon,
  Award as AwardIcon,
} from 'lucide-react';
import SearchBar from '../../../../ui/canon/SearchBar';
import SavedValuationCard from './SavedValuationCard';
import { gatherConfidenceFactors, calculateConfidence } from './ConfidenceRating';
import { S3Image } from '../../lib/s3';
import { currencySymbol } from '../../utils/formatters';

// ---------------------------------------------------------------------------
// Helper utilities
// ---------------------------------------------------------------------------

/**
 * Format a date string as a human-friendly relative time (e.g. "30 minutes ago").
 */
function formatRelativeDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months !== 1 ? 's' : ''} ago`;
  }
  const years = Math.floor(diffDays / 365);
  return `${years} year${years !== 1 ? 's' : ''} ago`;
}

/**
 * Format a currency value with abbreviation (e.g. "$9.6M").
 */
function formatCurrencyShort(value) {
  const sym = currencySymbol();
  if (value === undefined || value === null || !Number.isFinite(Number(value))) return `${sym}0`;
  const num = Number(value);
  const absValue = Math.abs(num);
  if (absValue >= 1e9) return `${sym}${(num / 1e9).toFixed(1)}B`;
  if (absValue >= 1e6) return `${sym}${(num / 1e6).toFixed(1)}M`;
  if (absValue >= 1e3) return `${sym}${(num / 1e3).toFixed(0)}K`;
  return `${sym}${num.toLocaleString()}`;
}

/**
 * Derive a methodology label from the scenario results weights.
 */
function getMethodologyString(results) {
  const weights = results?.weights || {};
  const activeMethods = [];
  if (Number(weights.mor || 0) > 0) activeMethods.push('MOR');
  if (Number(weights.dcf || 0) > 0) activeMethods.push('DCF');
  if (Number(weights.ebitda || 0) > 0) activeMethods.push('EBITDA');
  if (Number(weights.asset || 0) > 0) activeMethods.push('Asset');

  if (activeMethods.length > 1) {
    return `Multi-Line (${activeMethods.join(', ')})`;
  }
  if (activeMethods.length === 1) {
    return `${activeMethods[0]} Multiple`;
  }
  return 'Valuation';
}

/**
 * Compute the confidence level from a scenario's stored inputs & expenses.
 */
function computeConfidenceLevel(scenario) {
  try {
    const lineInputs = {};
    const lines = scenario.inputs?.lines || [];
    lines.forEach((l) => {
      if (l.name) lineInputs[l.name] = l.inputs || {};
    });
    const expensesInputs =
      scenario.expensesData || scenario.inputs?.expenses || null;
    const factors = gatherConfidenceFactors(lineInputs, expensesInputs);
    const rating = calculateConfidence(factors);
    return rating.overall;
  } catch {
    return 'medium'; // safe fallback
  }
}

/**
 * Sort an array of enriched valuation objects.
 */
function sortValuations(valuations, sortBy) {
  const sorted = [...valuations];
  switch (sortBy) {
    case 'name':
      return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    case 'confidence': {
      const confOrder = { high: 3, medium: 2, low: 1 };
      return sorted.sort(
        (a, b) => (confOrder[b.confidence] || 0) - (confOrder[a.confidence] || 0)
      );
    }
    case 'recent':
    default:
      return sorted.sort(
        (a, b) =>
          new Date(b.updatedAt || b.createdAt) -
          new Date(a.updatedAt || a.createdAt)
      );
  }
}

/**
 * Transform a raw GraphQL scenario into the enriched shape consumed by
 * SavedValuationCard.
 */
function enrichScenario(
  scenario,
  currentUserId,
  loadedScenarioId,
  validationData,
  currentUserAvatar
) {
  const results = scenario.results || {};
  const combined = results.combined || {};

  // Author type determination
  const authorId = scenario.author?.id;
  const isCurrentUser = !authorId || String(authorId) === String(currentUserId);
  const authorType = isCurrentUser ? 'user' : 'expert';

  // Author display info
  const authorName = isCurrentUser
    ? 'You'
    : scenario.author?.allowed_name || 'Expert';
  const authorAvatar = isCurrentUser
    ? (currentUserAvatar ? S3Image(currentUserAvatar) : null)
    : scenario.author?.profile_picture
      ? S3Image(scenario.author.profile_picture)
      : null;
  const authorCredentials =
    !isCurrentUser &&
    scenario.author?.designations &&
    scenario.author.designations.length > 0
      ? scenario.author.designations.join(', ')
      : null;

  // Methodology
  const methodology = getMethodologyString(results);

  // Confidence
  const confidence = computeConfidenceLevel(scenario);

  // Expert validation (if this scenario is the validated one)
  let expertValidation = null;
  if (
    validationData?.is_validated &&
    String(validationData.validated_scenario_id) === String(scenario.id)
  ) {
    expertValidation = {
      isValidated: true,
      isCurrentVersion: validationData.is_current_version !== false,
      validatedBy: {
        coachName: validationData.coach_profile?.name,
        credentials: validationData.coach_profile?.credentials?.join(', '),
        coachAvatar: validationData.coach_profile?.avatar_url,
      },
      validatedAt: validationData.validated_at,
      programName: validationData.program_name,
      validationNotes: validationData.validation_notes,
    };
  }

  return {
    id: scenario.id,
    name: scenario.name,
    isActive: String(scenario.id) === String(loadedScenarioId),
    author: {
      id: scenario.author?.id || currentUserId,
      name: authorName,
      type: authorType,
      avatar: authorAvatar,
      credentials: authorCredentials,
    },
    valuation: {
      value: formatCurrencyShort(combined.mid),
      low: formatCurrencyShort(combined.low),
      high: formatCurrencyShort(combined.high),
    },
    methodology,
    confidence,
    expertValidation,
    updatedAt: scenario.updatedAt || scenario.createdAt,
    createdAt: scenario.createdAt,
    updatedAtFormatted: formatRelativeDate(
      scenario.updatedAt || scenario.createdAt
    ),
    // Keep raw scenario reference for callbacks
    _original: scenario,
  };
}

// ---------------------------------------------------------------------------
// SavedValuationsPopover
// ---------------------------------------------------------------------------

/**
 * SavedValuationsPopover - A dropdown-style trigger that opens a popover panel
 * displaying the user's saved valuation scenarios, grouped into "My Valuations"
 * and "Expert Valuations", with search and sort controls.
 *
 * Ported from Polymet saved-valuations-popover.tsx → JSX
 *
 * @param {Object} props
 * @param {Array} props.scenarios - Raw scenario objects from GraphQL
 * @param {string|number} props.currentUserId - Current user's ID (for grouping)
 * @param {string|number|null} props.loadedScenarioId - Currently loaded scenario ID
 * @param {string|null} props.loadedScenarioName - Currently loaded scenario name
 * @param {Object|null} props.validationData - Expert validation data from valuationValidation query
 * @param {Function} props.onLoadScenario - Called with scenario ID when user selects a scenario
 * @param {Function} [props.onClearScenario] - Called to clear the loaded scenario
 * @param {Function} [props.onDuplicate] - Called with raw scenario when user duplicates
 * @param {Function} [props.onDelete] - Called with raw scenario when user deletes
 * @param {string} [props.currentUserAvatar] - Full URL of the current user's avatar (for "My Valuations" cards)
 * @param {React.ReactNode} [props.trigger] - Optional custom trigger element
 * @param {string} [props.className] - Additional CSS classes
 */
const SavedValuationsPopover = ({
  scenarios = [],
  currentUserId,
  currentUserAvatar,
  loadedScenarioId,
  loadedScenarioName,
  validationData,
  onLoadScenario,
  onClearScenario,
  onDuplicate,
  onDelete,
  trigger,
  className = '',
}) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');

  // Display name for the trigger button
  const currentName = loadedScenarioName || intl.formatMessage({ id: 'succession.multi_line_valuation.load_scenario' });

  // Enrich raw GraphQL scenarios into card-ready data
  const enrichedScenarios = useMemo(
    () =>
      scenarios.map((s) =>
        enrichScenario(s, currentUserId, loadedScenarioId, validationData, currentUserAvatar)
      ),
    [scenarios, currentUserId, loadedScenarioId, validationData, currentUserAvatar]
  );

  // Filter by search query
  const filteredValuations = useMemo(() => {
    let filtered = enrichedScenarios;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (v) =>
          (v.name || '').toLowerCase().includes(q) ||
          (v.author?.name || '').toLowerCase().includes(q) ||
          (v.methodology || '').toLowerCase().includes(q)
      );
    }

    return sortValuations(filtered, sortBy);
  }, [enrichedScenarios, searchQuery, sortBy]);

  // Group into My / Expert
  const myValuations = useMemo(
    () => filteredValuations.filter((v) => v.author?.type === 'user'),
    [filteredValuations]
  );
  const expertValuations = useMemo(
    () => filteredValuations.filter((v) => v.author?.type === 'expert'),
    [filteredValuations]
  );

  // Card click → load scenario, close popover
  const handleLoadValuation = (valuation) => {
    if (valuation._original?.id) {
      onLoadScenario && onLoadScenario(valuation._original.id);
    }
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      {/* ---- Trigger ---- */}
      {trigger ? (
        <div onClick={() => setOpen(!open)}>{trigger}</div>
      ) : (
        <button
          type="button"
          className="min-w-[200px] max-w-[280px] flex items-center justify-between gap-2 px-3 py-2 border border-succession-border rounded text-left bg-succession-bg-primary hover:bg-succession-bg-secondary transition-colors text-succession-text-primary"
          onClick={() => setOpen(!open)}
        >
          <span className="truncate text-sm">{currentName}</span>
          <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-succession-text-secondary" />
        </button>
      )}

      {/* ---- Popover ---- */}
      {open && (
        <>
          {/* Backdrop – click to close */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />

          {/* Panel */}
          <div
            className="absolute z-50 top-full right-0 mt-2 bg-succession-bg-primary border border-succession-border rounded-lg shadow-lg overflow-hidden"
            style={{ width: 600 }}
          >
            <div className="flex flex-col" style={{ height: 600 }}>
              {/* ---- Header ---- */}
              <div className="p-4 space-y-3 border-b border-succession-border flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-succession-text-primary">
                    Saved Valuations
                  </h3>
                  <span className="text-sm text-succession-text-secondary">
                    {filteredValuations.length}{' '}
                    {filteredValuations.length === 1 ? 'scenario' : 'scenarios'}
                  </span>
                </div>

                {/* Search */}
                <div onClick={(e) => e.stopPropagation()}>
                  <SearchBar
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search by name, author, or method..."
                    variant="outline"
                    size="md"
                  />
                </div>

                {/* Sort */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-succession-text-secondary">
                    Sort by:
                  </span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-2 py-1 h-8 border border-succession-border rounded bg-succession-bg-primary text-succession-text-primary text-sm focus:outline-none focus:ring-1 focus:ring-succession-primary"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="recent">Most Recent</option>
                    <option value="name">Name</option>
                    <option value="confidence">Confidence</option>
                  </select>
                </div>
              </div>

              {/* ---- Scrollable content ---- */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-4 space-y-6">
                  {/* Empty state */}
                  {filteredValuations.length === 0 && (
                    <div className="text-center py-12">
                      <FolderIcon className="w-12 h-12 mx-auto mb-3" style={{ color: '#9CA3AF80' }} />
                      <p className="text-sm text-succession-text-secondary">
                        No valuations found
                      </p>
                      {searchQuery && (
                        <button
                          type="button"
                          className="mt-2 text-sm text-succession-primary hover:underline"
                          onClick={() => setSearchQuery('')}
                        >
                          Clear search
                        </button>
                      )}
                    </div>
                  )}

                  {/* My Valuations */}
                  {myValuations.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm font-medium text-succession-text-secondary">
                        <UserIcon className="w-4 h-4" />
                        <span>My Valuations</span>
                        <span className="text-xs">({myValuations.length})</span>
                      </div>
                      <div className="space-y-2">
                        {myValuations.map((v) => (
                          <SavedValuationCard
                            key={v.id}
                            valuation={v}
                            onLoad={handleLoadValuation}
                            onDuplicate={
                              onDuplicate
                                ? () => onDuplicate(v._original)
                                : undefined
                            }
                            onDelete={
                              onDelete
                                ? () => onDelete(v._original)
                                : undefined
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Separator */}
                  {myValuations.length > 0 && expertValuations.length > 0 && (
                    <div className="border-t border-succession-border" />
                  )}

                  {/* Expert Valuations */}
                  {expertValuations.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm font-medium text-succession-text-secondary">
                        <AwardIcon className="w-4 h-4" />
                        <span>Expert Valuations</span>
                        <span className="text-xs">
                          ({expertValuations.length})
                        </span>
                      </div>
                      <div className="space-y-2">
                        {expertValuations.map((v) => (
                          <SavedValuationCard
                            key={v.id}
                            valuation={v}
                            onLoad={handleLoadValuation}
                            onDuplicate={
                              onDuplicate
                                ? () => onDuplicate(v._original)
                                : undefined
                            }
                            /* Expert valuations don't get delete */
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SavedValuationsPopover;
